import {
    AltitudeGroup,
    altitudeGroups,
    categories,
    ProductCategory,
    ProductCategoryItem,
    products,
    productsFlat,
    SelectedWeatherItem,
    SelectedWeatherItemType,
    WeatherProduct
} from '@fe/weather';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Checkbox, TextBox, TextBoxChangeEvent, TextBoxHandle } from '@progress/kendo-react-inputs';
import React from 'react';
import { FlyoutActions } from '../../app/features/home/flyouts/Flyout';
import { useAppDispatch, useAppSelector } from '../../app/hooks/store-hooks';
import { setSetting } from '../../app/state/slices/settings-slice';
import { ChevronDownOutline, ChevronUpOutline, CloseOutline, FavoriteOutline, FavoriteSolid, SearchOutline } from '../../icons/icons';
import useWeatherService from '../hooks/use-weather-service';
import useSelectedWeather from './hooks/use-selected-weather';

const MAX_DISPLAYED_PRODUCT_COUNT = 20;

enum CategoryItemType {
    product,
    altitudeGroup
}

interface CategoryItemProduct {
    type: CategoryItemType.product;
    item: WeatherProduct;
}

interface CategoryItemAltitudeGroup {
    type: CategoryItemType.altitudeGroup;
    item: AltitudeGroup;
    /** ID of a product from the product list that is the first in the list with that group id */
    firstProductId: number;
}

type CategoryItem = CategoryItemProduct | CategoryItemAltitudeGroup;

interface DisplayCategory {
    category: ProductCategoryItem;
    products: CategoryItem[];
}

const WeatherListContext = React.createContext<{
    favorite: CategoryItem[];
    selected: SelectedWeatherItem[];
    toggleSelected?: (item: CategoryItem, value: boolean) => void;
    toggleFavorite?: (item: CategoryItem, value: boolean) => void;
}>({
    favorite: [],
    selected: []
});

export default function WeatherFlyout() {
    const [searchText, setSearchText] = React.useState<string>(() => (import.meta.env.DEV ? (localStorage.getItem('search') ?? '') : ''));
    const [searchVisible, setSearchVisible] = React.useState(false);
    const authorizedProducts = useAppSelector((x) => x.weather.authorized);
    const isPlayback = useAppSelector((x) => x.global.playback.active);
    const availablePlaybackWeather = useAppSelector((x) => x.weather.playback.available);
    const displayCategories = React.useMemo(
        () => getDisplayData(isPlayback, authorizedProducts, availablePlaybackWeather),
        [authorizedProducts, availablePlaybackWeather, isPlayback]
    );
    const favoriteWeather = useAppSelector((state) => state.settings.favoriteWeather);
    const flatList = React.useMemo(() => displayCategories.map((x) => x.products).flat(), [displayCategories]);
    const favoriteProducts = React.useMemo(() => getFavorite(flatList, favoriteWeather), [flatList, favoriteWeather]);
    const selectedWeather = useSelectedWeather();
    const handleSearchTextUpdate = React.useCallback((e: TextBoxChangeEvent) => setSearchText(e.value as string), []);
    const weatherService = useWeatherService();
    const searchRef = React.createRef<TextBoxHandle | null>();
    const dispatch = useAppDispatch();

    const searchResults = React.useMemo(() => {
        const query = searchText.toLowerCase();
        if (import.meta.env.DEV) {
            localStorage.setItem('search', query);
        }
        // TODO: debounce
        const queryNumHex = +query;
        const queryNumDec = Number.parseInt(query, 16);
        const regex = new RegExp(RegExp.escape(query), 'i');
        return flatList.filter(
            (x) =>
                regex.test(x.item.name) ||
                (!Number.isNaN(queryNumHex) && x.item.id === queryNumHex) ||
                (!Number.isNaN(queryNumDec) && x.item.id === queryNumDec) ||
                regex.test(x.item.vendor)
        );
    }, [searchText, flatList]);

    React.useEffect(() => {
        if (searchVisible) {
            searchRef.current?.element?.focus();
        }
    }, [searchVisible]);

    const handleToggleFavorite = React.useCallback(
        (item: CategoryItem, value: boolean) => {
            const newItems = [...favoriteProducts];
            if (value) {
                newItems.push(item);
            } else {
                const idx = newItems.indexOf(item);
                if (idx > -1) {
                    newItems.splice(idx, 1);
                }
            }

            const ids = newItems.map((x) => (x.type === CategoryItemType.product ? x.item.id : x.firstProductId));
            dispatch(setSetting('favoriteWeather', ids));
        },
        [favoriteProducts]
    );

    const handleToggleWeather = React.useCallback(
        (item: CategoryItem, value: boolean) => {
            if (value) {
                if (item.type === CategoryItemType.product) {
                    weatherService!.addProduct(item.item.id, SelectedWeatherItemType.weather);
                } else {
                    // TODO: move the code that picks altitude into weather service and pick altitude here instead of choosing the first product from the list
                    // service will load this product then the altitude slider will change it 1ms later
                    const picked = weatherService.altitudePicker.pick(item.item.id);
                    if (picked) {
                        weatherService?.addProduct(picked.id, SelectedWeatherItemType.weather);
                    } else {
                        weatherService?.addProduct(item.firstProductId, SelectedWeatherItemType.weather);
                    }
                }
            } else {
                if (item.type === CategoryItemType.altitudeGroup) {
                    weatherService!.removeAltitudeGroup(item.item.id);
                } else {
                    weatherService!.removeProduct(item.item.id, SelectedWeatherItemType.weather);
                }
            }
        },
        [selectedWeather, weatherService]
    );

    const canAddMore = selectedWeather.length < MAX_DISPLAYED_PRODUCT_COUNT;
    return (
        <div className="d-flex flex-column overflow-auto" id="weather-products-container" style={{ width: 420 }}>
            <FlyoutActions>
                <Button
                    className="button-small"
                    svgIcon={SearchOutline}
                    fillMode={'flat'}
                    onClick={() => setSearchVisible(!searchVisible)}
                />
            </FlyoutActions>
            {/* {import.meta.env.DEV && <Button onClick={() => setSearchVisible((p) => !p)}>Toggle search</Button>} */}
            {searchVisible && (
                <div className="border-bottom p-2">
                    <TextBox
                        ref={searchRef}
                        value={searchText}
                        onChange={handleSearchTextUpdate}
                        placeholder="Search"
                        className="flex-shrink-0"
                        rounded={'large'}
                        suffix={() => (
                            <SvgIcon className="my-auto me-2 cursor-pointer" icon={CloseOutline} onClick={() => setSearchText('')} />
                        )}
                        prefix={() => <SvgIcon className="my-auto ms-2" icon={SearchOutline} />}
                    />
                </div>
            )}

            <WeatherListContext.Provider
                value={{
                    selected: selectedWeather,
                    favorite: favoriteProducts,
                    toggleSelected: handleToggleWeather,
                    toggleFavorite: handleToggleFavorite
                }}>
                <div className="d-flex flex-column gap-2 overflow-auto p-2">
                    {isPlayback && <div className="secondary-text">Only recorded weather products are available in playback mode.</div>}
                    {searchText.length === 0 && (
                        <>
                            {!isPlayback && (
                                <WeatherCategoryFlyoutCategory
                                    item={{
                                        category: {
                                            id: ProductCategory.none,
                                            name: 'Favorites'
                                        },
                                        products: favoriteProducts
                                    }}
                                    enabled={canAddMore}
                                />
                            )}

                            {displayCategories.map((item) => (
                                <WeatherCategoryFlyoutCategory key={item.category.id} item={item} enabled={canAddMore} />
                            ))}
                        </>
                    )}

                    {searchText.length > 0 && (
                        <>
                            {searchResults.map((item, i) => (
                                <WeatherFlyoutProduct key={i} item={item} enabled={canAddMore} />
                            ))}
                            {searchResults.length === 0 && <div>No results.</div>}
                        </>
                    )}
                </div>
            </WeatherListContext.Provider>
        </div>
    );
}

const WeatherCategoryFlyoutCategory = (props: { item: DisplayCategory; enabled: boolean }) => {
    const { item, enabled } = props;
    const [expanded, setExpanded] = React.useState(false);

    return (
        <div
            className="cursor-pointer flex-shrink-0 weather-category-item"
            style={{
                backgroundColor: 'light-dark(var(--color-neutral-800), var(--color-neutral-100))',
                borderRadius: '8px',
                minHeight: '38px'
            }}>
            <div
                onClick={() => setExpanded((x) => !x)}
                className="flex-grow-1 d-flex align-items-center ps-1 px-2 py-1 weather-category-item-header"
                style={{ height: 38 }}>
                {item.category.image && <img height={30} src={item.category.image} alt="cat-image" style={{ borderRadius: '4px' }}></img>}
                {!item.category.image && <SvgIcon height={20} className="ms-2" icon={FavoriteSolid} />}

                <span className="px-2 flex-grow-1">{item.category.name}</span>
                <SvgIcon icon={expanded ? ChevronUpOutline : ChevronDownOutline} className="button-small" />
            </div>

            {expanded && (
                <div className="px-2 py-1">
                    {item.products.map((element, i) => (
                        <WeatherFlyoutProduct key={i} enabled={enabled} item={element} />
                    ))}
                    {item.products.length === 0 && <span className="secondary-text">No items.</span>}
                </div>
            )}
        </div>
    );
};

const WeatherFlyoutProduct = (props: { item: CategoryItem; enabled: boolean }) => {
    const context = React.useContext(WeatherListContext);
    let selected = false;
    if (props.item.type === CategoryItemType.product) {
        selected = context.selected.some((x) => x.id === props.item.item.id && x.type === SelectedWeatherItemType.weather);
    } else {
        selected = context.selected.some((x) => x.product.group === props.item.item.id && x.type === SelectedWeatherItemType.weather);
    }
    const favorite = context.favorite.includes(props.item);

    return (
        <div className="d-flex gap-2 align-items-start product-item">
            <Checkbox
                checked={selected}
                label={
                    <span className="ps-1">
                        {import.meta.env.DEV && (
                            <span>
                                (0x{props.item.item.id?.toString(16)}
                                {props.item.type === CategoryItemType.altitudeGroup && <span> group</span>}){' '}
                            </span>
                        )}
                        <span className="flex-grow-1">
                            {props.item.item.name} [{props.item.item.vendor}]
                        </span>
                    </span>
                }
                disabled={!selected && !props.enabled}
                onChange={(e) => context.toggleSelected?.(props.item, e.value)}
            />
            <div className="flex-grow-1"></div>
            <Button
                svgIcon={favorite ? FavoriteSolid : FavoriteOutline}
                className="button-small weather-favorite-btn"
                fillMode={'flat'}
                onClick={() => context.toggleFavorite?.(props.item, !favorite)}
            />
        </div>
    );
};

function getDisplayData(isPlayback: boolean, authorizedList: number[], playbackWeather: number[]): DisplayCategory[] {
    const flat = isPlayback
        ? productsFlat.filter((x) => playbackWeather.includes(x.id))
        : productsFlat.filter((x) => authorizedList.includes(x.id));

    const data: DisplayCategory[] = [];

    for (const category of categories) {
        if (!import.meta.env.DEV && category.hidden) {
            continue;
        }

        const products = flat.filter((x) => x.category === category.id);
        if (products.length === 0) {
            continue;
        }

        const displayProducts: CategoryItem[] = [];
        const includedGroups: number[] = [];

        for (const product of products) {
            if (product.group === null) {
                displayProducts.push({
                    type: CategoryItemType.product,
                    item: product
                });
                continue;
            }

            if (includedGroups.includes(product.group)) {
                continue;
            }

            includedGroups.push(product.group);
            const group = altitudeGroups[product.group];
            if (group) {
                displayProducts.push({
                    type: CategoryItemType.altitudeGroup,
                    item: group,
                    firstProductId: product.id
                });
            } else {
                // Fall back for products that have group id but don't have a group defined
                displayProducts.push({
                    type: CategoryItemType.product,
                    item: product
                });
            }
        }

        const item: DisplayCategory = {
            category: category,
            products: displayProducts
        };

        data.push(item);
    }
    return data;
}

function getFavorite(flatList: CategoryItem[], favorite: number[]): CategoryItem[] {
    const favoriteProducts = favorite.map((x) => products[x]).filter(Boolean);

    const groupIds = favoriteProducts.reduce((previous, next) => {
        if (next.group !== null) {
            if (!previous.includes(next.group)) {
                previous.push(next.group);
            }
        }
        return previous;
    }, [] as number[]);

    // Groups
    const favGroups = flatList.filter((x) => x.type === CategoryItemType.altitudeGroup && groupIds.includes(x.item.id));

    // Products
    const favProducts = flatList.filter((x) => x.type === CategoryItemType.product && favorite.includes(x.item.id));

    return [...favProducts, ...favGroups];
}
